import { CardElementName } from "../../models/CardElement";
import { CardVariant } from "../../models/CardVariant";
import { ComboBoxOption } from "../../models/Input";
import { PriceDictionary } from "../../models/Price";
import { ICardSortCommand } from "./ICardSortCommand";

export type SortCardsArgs = {
    sortType: SortType,
    prices?: PriceDictionary
}

export class SortCards implements ICardSortCommand<SortCardsArgs> {
    sort<TItem extends CardVariant>(items: TItem[], args: SortCardsArgs): TItem[] {
        if (!items) {
            return [];
        }

        return items.sort((a, b) => {
            switch (args.sortType) {
                case "Cost (Asc)":
                    return (a.card.cost ?? 0) - (b.card.cost ?? 0);
                case "Cost (Desc)":
                    return (b.card.cost ?? 0) - (a.card.cost ?? 0);
                case "Name (Asc)":
                    return (a.card.name ?? "") < (b.card.name ?? "") ? -1 : 1 || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0);
                case "Name (Desc)":
                    return (a.card.name ?? "") > (b.card.name ?? "") ? -1 : 1 || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0);
                case "Opus (Asc) / Number (Asc)":
                    return (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0);
                case "Opus (Asc) / Number (Desc)":
                    return (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (b.altCardNumber ?? b.cardNumber ?? 0) - (a.altCardNumber ?? a.cardNumber ?? 0);
                case "Opus (Desc) / Number (Asc)":
                    return (b.altOpus ?? b.opus ?? 0) - (a.altOpus ?? a.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0);
                case "Opus (Desc) / Number (Desc)":
                    return (b.altOpus ?? b.opus ?? 0) - (a.altOpus ?? a.opus ?? 0) || (b.altCardNumber ?? b.cardNumber ?? 0) - (a.altCardNumber ?? a.cardNumber ?? 0);
                case "Element (Asc)":
                    return (getElementSortNumber(a)) - (getElementSortNumber(b)) || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0);
                case "Price (Asc)":
                    if (args?.prices) {
                        const aPrice = args.prices[a.id];
                        const bPrice = args.prices[b.id];

                        return (aPrice?.price ?? 0) - (bPrice?.price ?? 0);
                    } else {
                        return 0;
                    }
                case "Price (Desc)":
                    if (args?.prices) {
                        const aPrice = args.prices[a.id];
                        const bPrice = args.prices[b.id];

                        return (bPrice?.price ?? 0) - (aPrice?.price ?? 0);
                    } else {
                        return 0;
                    }
                default:
                    return 0;
            }
        });
    }
}

function getElementSortNumber<TItem extends CardVariant>(variant: TItem): number {
    if (!variant.card || !variant.card.elements || !variant.card.elements.length) {
        return 0;
    }

    if (variant.card.elements.length > 1) {
        return 7;
    }

    return elementSortValueMap[variant.card.elements[0]];
}

const elementSortValueMap: Record<CardElementName, number> = {
    "Fire": 1,
    "Ice": 2,
    "Wind": 3,
    "Earth": 4,
    "Lightning": 5,
    "Water": 6,
    "Light": 1000,
    "Dark": 1000
}


export type SortType = "Cost (Asc)" | "Cost (Desc)" | "Opus (Asc) / Number (Asc)" | "Opus (Desc) / Number (Asc)" | "Opus (Asc) / Number (Desc)" | "Opus (Desc) / Number (Desc)" | "Name (Asc)" | "Name (Desc)" | "Element (Asc)" | "Price (Asc)" | "Price (Desc)";

export function getSortByOptions(): ComboBoxOption<SortType>[] {
    return [
        { label: "Cost (Asc)", value: "Cost (Asc)" },
        { label: "Cost (Desc)", value: "Cost (Desc)" },
        { label: "Opus (Asc) / Number (Asc)", value: "Opus (Asc) / Number (Asc)" },
        { label: "Opus (Asc) / Number (Desc)", value: "Opus (Asc) / Number (Desc)" },
        { label: "Opus (Desc) / Number (Asc)", value: "Opus (Desc) / Number (Asc)" },
        { label: "Opus (Desc) / Number (Desc)", value: "Opus (Desc) / Number (Desc)" },
        { label: "Name (Asc)", value: "Name (Asc)" },
        { label: "Name (Desc)", value: "Name (Desc)" },
        { label: "Element (Asc)", value: "Element (Asc)" },
        { label: "Price (Asc)", value: "Price (Asc)" },
        { label: "Price (Desc)", value: "Price (Desc)" }
    ];
}